import React, { ReactNode, useEffect, useState } from 'react';
import { PostHogProvider as PostHogReactProvider } from '@posthog/react/slim';
import { useLocation } from 'react-router-dom';
import {
  analyticsClient,
  subscribePostHogReady,
  syncPostHogSessionRecordingForPathname,
} from '../analytics/analyticsClient';
import { applyInternalTrafficFlag } from '../analytics/internalTraffic';
import { analyticsSafeHref } from '../analytics/sanitizeAnalyticsUrl';
import { persistUtmsFromSearch } from '../analytics/utm';

export {
  QI_INTERNAL_STORAGE_KEY,
  applyInternalTrafficFlag,
  resolveInternalFlag,
  stripInternalQueryParamFromUrl,
} from '../analytics/internalTraffic';

export { analyticsClient as posthog } from '../analytics/analyticsClient';

/** Last key seen — only suppresses an immediate consecutive duplicate (e.g. StrictMode). */
let lastConsecutivePageviewKey: string | null = null;

/** @internal test helper */
export function resetPageviewDedupeForTests(): void {
  lastConsecutivePageviewKey = null;
}

/**
 * Fires a $pageview event on every React Router navigation. Must be
 * rendered inside the Router (e.g. in App.tsx).
 */
export function trackRouterPageview(search: string): void {
  applyInternalTrafficFlag(search);
  const utms = persistUtmsFromSearch(search);
  if (!process.env.REACT_APP_POSTHOG_KEY) return;

  const key = `${window.location.pathname}|${search}|${window.location.hash}`;
  if (lastConsecutivePageviewKey === key) return;
  lastConsecutivePageviewKey = key;

  if (Object.keys(utms).length > 0) {
    analyticsClient.register(utms);
  }
  analyticsClient.capture('$pageview', {
    $current_url: analyticsSafeHref(),
    ...utms,
  });
}

export function PostHogPageviewTracker() {
  const location = useLocation();

  useEffect(() => {
    trackRouterPageview(location.search);
    syncPostHogSessionRecordingForPathname(location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  return null;
}

type PostHogJsClient = import('posthog-js').PostHog;

/** Inert stand-in until analyticsClient fires subscribePostHogReady; keeps tree shape stable. */
// usePostHog / useFeatureFlag callers must tolerate pre-ready `{}` (not a real client); no flag reads (e.g. headline A/B, #462) before ready.
const POSTHOG_CLIENT_PLACEHOLDER = {} as PostHogJsClient;

export function PostHogProvider({ children }: { children: ReactNode }) {
  const [client, setClient] = useState<PostHogJsClient | null>(null);

  useEffect(() => {
    void import('@posthog/react');
    return subscribePostHogReady((readyClient) => {
      if (!readyClient) return;
      setClient(readyClient as PostHogJsClient);
    });
  }, []);

  if (!process.env.REACT_APP_POSTHOG_KEY) {
    return <>{children}</>;
  }

  return (
    <PostHogReactProvider client={client ?? POSTHOG_CLIENT_PLACEHOLDER}>
      {children}
    </PostHogReactProvider>
  );
}

export default PostHogProvider;
